// Паттерны фона блока — вторая, НЕЗАВИСИМАЯ от темы ось оформления
// (`patternType` у блока, см. docs/backend/gamified-lesson-theme-and-block-pattern.md).
// Геометрия снята с векторных экспортов дизайнера (docs/design/patterns/*.svg),
// холст 1654×2609 — это 4× экрана ≈413×652, поэтому размеры тайлов заданы в
// ПРОЦЕНТАХ ширины канваса: превью блока бывает любой ширины, а масштаб узора
// обязан оставаться тем же, что в макете.
//
// Цвет узора — не прозрачность поверх фона, а СВОЙ оттенок на каждую пару
// «тема × паттерн» (у кругов на кремовых темах он светлее). Наружу уходят
// готовые CSS-значения, как и у LESSON_THEMES: `hsl()`/токены к ним неприменимы.
import { DEFAULT_LESSON_THEME, LESSON_THEMES, type LessonThemeId } from './lessonThemes'
import { uiText } from '../i18n'
import { STROKES_PATH, STROKES_VIEWBOX, STROKES_WIDTH } from './strokesPath'

export type BlockPatternId = 'diamonds' | 'strokes' | 'circles'

/** Подпись читается геттером — на языке интерфейса в момент обращения. */
const LABELS = {
  ru: { diamonds: 'Ромбы', strokes: 'Штрихи', circles: 'Круги' },
  en: { diamonds: 'Diamonds', strokes: 'Strokes', circles: 'Circles' },
}

export const BLOCK_PATTERNS: readonly { id: BlockPatternId; label: string }[] = [
  {
    id: 'diamonds',
    get label() {
      return uiText(LABELS).diamonds
    },
  },
  {
    id: 'strokes',
    get label() {
      return uiText(LABELS).strokes
    },
  },
  {
    id: 'circles',
    get label() {
      return uiText(LABELS).circles
    },
  },
] as const

/** Бэк значение не валидирует (контракт, раздел «Валидация»): незнакомое
 *  сохраняется и возвращается как есть, и читать его надо как «не задано». */
export const resolvePatternId = (value: string | null | undefined): BlockPatternId | null =>
  BLOCK_PATTERNS.find((p) => p.id === value)?.id ?? null

interface Ink {
  hex: string
  opacity: number
}

const CREAM: Record<BlockPatternId, Ink> = {
  diamonds: { hex: '#FFF1E7', opacity: 1 },
  strokes: { hex: '#FFF1E7', opacity: 1 },
  circles: { hex: '#FFF5EE', opacity: 1 },
}
const LAVENDER: Ink = { hex: '#F0E6FF', opacity: 0.5 }

const INK: Record<LessonThemeId, Record<BlockPatternId, Ink>> = {
  theme1: CREAM,
  theme2: CREAM,
  theme3: { diamonds: LAVENDER, strokes: LAVENDER, circles: LAVENDER },
}

const svgUrl = (svg: string): string => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`

const CANVAS_W = 1654

// Округление до сотых: иначе в путь утекают хвосты вида 163.17000000000002.
const r2 = (n: number): number => Math.round(n * 100) / 100

const diamond = (x: number, y: number): string =>
  `M${x} ${r2(y - 31.82)}L${r2(x + 31.82)} ${y}L${x} ${r2(y + 31.82)}L${r2(x - 31.82)} ${y}Z`

const tile = (w: number, h: number, body: string): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`

// Центры ромбов в тайле 172×296 — ровно как в SVG макета: первый ромб стоит
// с отступом от края блока, а не режется им. Оба целиком внутри тайла, краевые
// копии не нужны.
const DIAMOND_CENTERS: readonly (readonly [number, number])[] = [
  [52.82, 194.99],
  [136.82, 46.99],
]

const diamondsSvg = ({ hex, opacity }: Ink): string =>
  tile(
    172,
    296,
    `<path fill="${hex}" fill-opacity="${opacity}" d="${DIAMOND_CENTERS.map(([x, y]) =>
      diamond(x, y),
    ).join('')}"/>`,
  )

// Кольцо: отверстие 54.5, внешний край 107.5 (r=81, обводка 53), шаг 211×210,
// центр первого — (88.5, 149.5). Кольцо больше тайла и заходит в соседние,
// поэтому рисуется вместе с восемью копиями со сдвигом на ±шаг — иначе на
// стыках тайлов кольца рвутся.
const circlesSvg = ({ hex, opacity }: Ink): string =>
  tile(
    211,
    210,
    [-1, 0, 1]
      .flatMap((dx) => [-1, 0, 1].map((dy) => [88.5 + dx * 211, 149.5 + dy * 210]))
      .map(
        ([cx, cy]) =>
          `<circle cx="${cx}" cy="${cy}" r="81" fill="none" stroke="${hex}" stroke-opacity="${opacity}" stroke-width="53"/>`,
      )
      .join(''),
  )

const strokesSvg = ({ hex, opacity }: Ink): string =>
  // width/height обязательны: без них `100% auto` не знает пропорции холста.
  `<svg xmlns="http://www.w3.org/2000/svg" width="1654" height="2609" viewBox="${STROKES_VIEWBOX}" fill="none">` +
  `<path d="${STROKES_PATH}" stroke="${hex}" stroke-opacity="${opacity}" stroke-width="${STROKES_WIDTH}" stroke-linecap="round"/></svg>`

const STROKES_LAYER_GEOMETRY = '0 0/100% auto repeat-y'

const pct = (px: number): string => `${Math.round((px / CANVAS_W) * 1000) / 10}%`

/** CSS-слой паттерна для КАНВАСА блока. `null` — паттерна нет. */
export const patternLayer = (
  theme: LessonThemeId,
  pattern: BlockPatternId | null,
): string | null => {
  if (!pattern) return null
  const ink = INK[theme][pattern]
  if (pattern === 'diamonds') return `${svgUrl(diamondsSvg(ink))} 0 0/${pct(172)} auto repeat`
  if (pattern === 'circles') return `${svgUrl(circlesSvg(ink))} 0 0/${pct(211)} auto repeat`
  // Штрихи — россыпь на весь холст 1654×2609. Масштаб, как у ромбов и кругов,
  // — по ширине блока (холст = ширина блока); ниже высоты холста рисунок
  // повторяется. `cover` растягивал его по высоте высокого блока, штрихи
  // выходили крупнее макета, а края обрезались.
  return `${svgUrl(strokesSvg(ink))} ${STROKES_LAYER_GEOMETRY}`
}

const backgroundOf = (theme: LessonThemeId): string =>
  (LESSON_THEMES.find((t) => t.id === theme) ?? DEFAULT_LESSON_THEME).background

// Образец 20×20 в карточке выбора — ОПОЗНАВАТЕЛЬНЫЙ ЗНАК ФОРМЫ, а не проба
// цвета: у краски канваса светлота отличается от фона на 1–2 % (так нарисовано
// в макете), и на квадрате 20×20 «Без паттерна», «Ромбы», «Штрихи» и «Круги»
// выглядели четырьмя одинаковыми кремовыми квадратами — выбор было не отличить
// (живой прогон 2026-08-17). Поэтому у образца СВОЯ подача: краска заметно
// темнее фона (одна на тему — на образце важна форма, а не оттенок узора) и
// свой, более крупный шаг тайла. На канвасе блока по-прежнему INK: там узор
// читается за счёт размера и обязан оставаться таким, как его нарисовал дизайнер.
const SWATCH_INK: Record<LessonThemeId, Ink> = {
  theme1: { hex: '#E5C29C', opacity: 1 },
  theme2: { hex: '#E5A77F', opacity: 1 },
  theme3: { hex: '#8E77D8', opacity: 1 },
}

// Шаг тайла образца в ПИКСЕЛЯХ (процент от ширины дал бы узор в пару пикселей).
// У штрихов он на порядок крупнее: в слое 760 отрезков толщиной 12 на канвас
// 1654, и при тайле в 40px это волоски в 0.3px — на 138px они становятся
// дужками в пиксель, а в окошко попадает около десятка штрихов.
const SWATCH_TILE: Record<BlockPatternId, number> = { diamonds: 18, circles: 20, strokes: 138 }

/** `background` для образца 20×20 в карточке выбора. */
export const patternSwatch = (theme: LessonThemeId, pattern: BlockPatternId | null): string => {
  const bg = backgroundOf(theme)
  if (!pattern) return bg
  const ink = SWATCH_INK[theme]
  const size = `${SWATCH_TILE[pattern]}px`
  if (pattern === 'diamonds') return `${svgUrl(diamondsSvg(ink))} 0 0/${size} auto repeat, ${bg}`
  if (pattern === 'circles') return `${svgUrl(circlesSvg(ink))} 0 0/${size} auto repeat, ${bg}`
  return `${svgUrl(strokesSvg(ink))} 50% 50%/${size} auto repeat, ${bg}`
}

// Прозрачность краски на мини-экране карточки выбора.
const CARD_INK_OPACITY = 0.35

/**
 * Слой узора для МИНИ-ЭКРАНА в карточке выбора оформления: геометрия канваса
 * (размер тайла в процентах ширины — узор масштабируется вместе с карточкой и
 * лежит так же, как на экране), но краска образца (`SWATCH_INK`): канвасная на
 * карточке в четыре раза уже телефона почти не видна.
 */
export const patternCardLayer = (
  theme: LessonThemeId,
  pattern: BlockPatternId | null,
): string | null => {
  if (!pattern) return null
  // Краска образца, но приглушённая: в полную силу узор на мини-экране спорит с фоном.
  const ink = { ...SWATCH_INK[theme], opacity: CARD_INK_OPACITY }
  if (pattern === 'diamonds') return `${svgUrl(diamondsSvg(ink))} 0 0/${pct(172)} auto repeat`
  if (pattern === 'circles') return `${svgUrl(circlesSvg(ink))} 0 0/${pct(211)} auto repeat`
  return `${svgUrl(strokesSvg(ink))} ${STROKES_LAYER_GEOMETRY}`
}
